import type { PillTone } from '../../components/ui/Pill';
import type {
  ShippingPromotion,
  ShippingPromotionFee,
  ShippingPromotionInput,
  ShippingPromotionKind,
  ShippingPromotionStatus,
} from '../../lib/api/operatorConsole';
import { formatAmount } from '../Shippings/formatMoney';

interface Option<T extends string> {
  value: T;
  label: string;
}

export const KIND_OPTIONS: Option<ShippingPromotionKind>[] = [
  { value: 'fixed_price', label: 'Fixed price' },
  { value: 'percent_off', label: 'Percent off' },
  { value: 'amount_off', label: 'Amount off' },
];

/** The parts of a delivery's price a promotion can cover, in the order the API lists them. */
export const FEE_OPTIONS: (Option<ShippingPromotionFee> & { hint: string })[] = [
  { value: 'shipping', label: 'Shipping', hint: 'The carrier rate of the delivery.' },
  { value: 'insurance', label: 'Insurance', hint: 'The add-on, when the player picks an option with it.' },
  { value: 'signature', label: 'Signature', hint: 'The add-on, when the player picks an option with it.' },
  { value: 'handling', label: 'Handling', hint: 'The per-card handling fee.' },
];

/** What the single value of each kind is called, and what it means. */
export const VALUE_FIELD: Record<
  ShippingPromotionKind,
  { label: string; hint: string; missing: string }
> = {
  fixed_price: {
    label: 'Price players pay',
    hint: 'What a player pays for the covered fees of one delivery. 0 makes them free.',
    missing: 'Enter the price players pay.',
  },
  amount_off: {
    label: 'Amount off',
    hint: 'Taken off the covered fees of each delivery.',
    missing: 'Enter the amount off.',
  },
  percent_off: {
    label: 'Percent off',
    hint: 'Taken off the covered fees of each delivery, from above 0 up to 100.',
    missing: 'Enter the percent off.',
  },
};

/** The option a raw <select> value stands for, or `fallback` for anything else. */
export function pick<T extends string>(options: Option<T>[], value: string, fallback: T): T {
  return options.find((option) => option.value === value)?.value ?? fallback;
}

const STATUS_BADGE: Record<ShippingPromotionStatus, { label: string; tone: PillTone }> = {
  scheduled: { label: 'Scheduled', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  ended: { label: 'Ended', tone: 'neutral' },
  disabled: { label: 'Disabled', tone: 'warning' },
};

export function statusBadge(status: ShippingPromotionStatus): { label: string; tone: PillTone } {
  // A status the backend adds later still renders, as itself.
  return STATUS_BADGE[status] ?? { label: status, tone: 'neutral' };
}

export function formatValue(
  promotion: Pick<ShippingPromotion, 'kind' | 'amount' | 'percent_off'>,
  currency: string,
): string {
  switch (promotion.kind) {
    case 'fixed_price':
      return `Fixed price ${formatAmount(promotion.amount, currency)}`;
    case 'amount_off':
      return `${formatAmount(promotion.amount, currency)} off`;
    case 'percent_off':
      return promotion.percent_off === null ? '—' : `${promotion.percent_off}% off`;
  }
}

const coveredFees = (covers: ShippingPromotionFee[]) => FEE_OPTIONS.filter((option) => covers.includes(option.value));

export function formatCovers(covers: ShippingPromotionFee[]): string {
  const [first, ...rest] = coveredFees(covers).map((option) => option.label);
  if (first === undefined) return '—';
  if (rest.length === 0) return `${first} only`;
  return [first, ...rest.map((label) => label.toLowerCase())].join(' + ');
}

export function formatCap(maxDiscount: number | null, currency: string): string {
  return maxDiscount === null ? 'No cap' : formatAmount(maxDiscount, currency);
}

export function formatLocalTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
}

const pad = (part: number) => String(part).padStart(2, '0');

/** An ISO instant as the `YYYY-MM-DDTHH:mm` a datetime-local input shows, in browser time. */
export function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** The ISO UTC instant a datetime-local value means in browser time; null when empty or unreadable. */
export function fromLocalInput(value: string): string | null {
  if (!value) return null;
  // No offset in the string, so Date reads it as local time.
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** The form's fields as typed: numbers stay text until submit. */
export interface PromotionDraft {
  label: string;
  kind: ShippingPromotionKind;
  amount: string;
  percentOff: string;
  covers: ShippingPromotionFee[];
  maxDiscount: string;
  startsAt: string;
  endsAt: string;
}

export function emptyDraft(): PromotionDraft {
  return {
    label: '',
    kind: 'fixed_price',
    amount: '',
    percentOff: '',
    covers: ['shipping', 'handling'],
    maxDiscount: '',
    startsAt: '',
    endsAt: '',
  };
}

const money = (value: number | null) => (value === null ? '' : Number(value).toFixed(2));

export function draftFromPromotion(promotion: ShippingPromotion): PromotionDraft {
  return {
    label: promotion.label,
    kind: promotion.kind,
    amount: money(promotion.amount),
    percentOff: promotion.percent_off === null ? '' : String(promotion.percent_off),
    covers: promotion.covers,
    maxDiscount: money(promotion.max_discount),
    startsAt: toLocalInput(promotion.starts_at),
    endsAt: toLocalInput(promotion.ends_at),
  };
}

// Blank is "not given" (null); text that is not a number is NaN.
function parseNumber(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : Number.NaN;
}

export type DraftResult = { ok: true; input: ShippingPromotionInput } | { ok: false; error: string };

const fail = (error: string): DraftResult => ({ ok: false, error });

/** Checks only what must exist to build the request; the ranges and overlaps are the backend's call. */
export function draftToInput(draft: PromotionDraft): DraftResult {
  const label = draft.label.trim();
  if (!label) return fail('Enter a label for the promotion.');

  const usesPercent = draft.kind === 'percent_off';
  const value = parseNumber(usesPercent ? draft.percentOff : draft.amount);
  if (value === null || Number.isNaN(value)) return fail(VALUE_FIELD[draft.kind].missing);

  const covers = coveredFees(draft.covers).map((option) => option.value);
  if (covers.length === 0) return fail('Choose at least one fee the promotion covers.');

  const cap = parseNumber(draft.maxDiscount);
  if (cap !== null && Number.isNaN(cap)) {
    return fail('Max discount must be a number, or empty for no cap.');
  }

  const startsAt = fromLocalInput(draft.startsAt);
  const endsAt = fromLocalInput(draft.endsAt);
  if (!startsAt || !endsAt) return fail('Set both the start and the end of the promotion.');

  return {
    ok: true,
    input: {
      label,
      kind: draft.kind,
      amount: usesPercent ? null : value,
      percent_off: usesPercent ? value : null,
      covers,
      max_discount: cap,
      starts_at: startsAt,
      ends_at: endsAt,
    },
  };
}
